import { DestroyRef, inject, Service } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Observable, of, Subject } from 'rxjs';

import { ToastService } from '@ideals/components/toast';
import { LoggerService } from '@ideals/core/logger';
import { PlatformService } from '@ideals/core/platform';

import { HttpError, RequestError, RequestErrorDetails } from '../../models';
import { isTranslatedErrorDetail } from '../../utils/normalize-http-error';

export function extractErrorMessage(errors: RequestErrorDetails = {}): string {
  return Object
    .keys(errors)
    .map((key) => errors[key]?.at(0)?.detail)
    .at(0) ?? 'Unknown error message';
}

export type ErrorInterceptorResult = 'complete' | 'retry' | 'throw';

type ErrorHandlerFn = (params: { error: HttpError | RequestError; next: () => void; }) => void;
type ErrorInterceptorFn = (
  error: RequestError,
  next: () => Observable<ErrorInterceptorResult>,
) => Observable<ErrorInterceptorResult>;

export function isRequestError(error: HttpError | RequestError): error is RequestError {
  return (error as RequestError).errorCode !== undefined;
}

export function isHttpError(error: HttpError | RequestError): error is HttpError {
  return !isRequestError(error);
}

@Service()
export class ErrorsService {
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #loggerService = inject(LoggerService);
  readonly #platformService = inject(PlatformService);
  readonly #router = inject(Router);
  readonly #toastService = inject(ToastService);

  readonly #error$ = new Subject<HttpError | RequestError>();

  #handlers: ErrorHandlerFn[] = [this.#defaultHandler.bind(this) as ErrorHandlerFn];
  #interceptors: ErrorInterceptorFn[] = [];

  constructor() {
    this.#error$.subscribe((error) => {
      const handlers = [...this.#handlers].reverse();
      const handle = (index: number): void => {
        handlers[index]?.({
          error,
          next: () => handle(index + 1),
        });
      };

      handle(0);
    });
  }

  handle(): Observable<{ error: HttpError | RequestError; next: () => void; }> {
    let handler: ErrorHandlerFn | undefined = undefined;

    const observable = new Observable<{ error: HttpError | RequestError; next: () => void; }>((subscriber) => {
      handler = ({ error, next }) => {
        subscriber.next({ error, next });
      };

      this.#handlers.push(handler);
    });

    return observable.pipe(
      finalize(() => {
        this.#handlers = this.#handlers.filter((h) => h !== handler);
      }),
    );
  }

  handleError(error: HttpError | RequestError): void {
    this.#error$.next(error);
    this.#loggerService.handleHttpError(error);
  }

  intercept(destroyRef: DestroyRef, interceptor: ErrorInterceptorFn): void {
    this.#interceptors.push(interceptor);

    destroyRef.onDestroy(() => {
      this.#interceptors = this.#interceptors.filter((registered) => registered !== interceptor);
    });
  }

  interceptError(error: RequestError): Observable<ErrorInterceptorResult> {
    const interceptors = [...this.#interceptors].reverse();
    const run = (index: number): Observable<ErrorInterceptorResult> => {
      return interceptors[index]?.(error, () => run(index + 1)) ?? of('throw');
    };

    return run(0);
  }

  #defaultHandler({ error }: { error: HttpError | RequestError; }): void {
    const defaultMessage = 'common.Error.Something_went_wrong';
    let troubleshootingSuggestion = false;
    let message = '';

    switch (error.status) {
      case 'No Response': {
        message = 'common.ERROR.no_internet';
        troubleshootingSuggestion = !this.#platformService.isMobile();
        break;
      }
      case 'Bad Request': {
        if (isHttpError(error)) {
          if (error.type === 'Detailed') {
            return;
          }

          message = error.message;
        } else {
          // Backend `detail` is a technical string; only show it when it was mapped to a translation
          message = isTranslatedErrorDetail(error.detail) ? error.detail : defaultMessage;
        }

        break;
      }
      case 'Unauthorized': {
        return;
      }
      case 'Forbidden': {
        message = 'common.ERROR.insufficient_permissions';
        break;
      }
      case 'Not Found': {
        message = error.method === 'GET' ? 'common.ERROR.page_not_found' : defaultMessage;
        break;
      }
      default: {
        message = defaultMessage;
        troubleshootingSuggestion = !this.#platformService.isMobile();
      }
    }

    const config = {
      buttons: [{
        label: 'help_modal.Toaster.Go_to_troubleshooting',
        command: () => {
          this.#router.navigate([], {
            queryParams: { help: 'troubleshooting' },
            queryParamsHandling: 'merge',
            relativeTo: this.#activatedRoute,
            replaceUrl: true,
          });
        },
      }],
    };

    this.#toastService.error(message, troubleshootingSuggestion ? config : {});
  }
}
